import fs from 'fs';
import path from 'path';
import chalk from 'chalk';

import { MD } from '../util/const';
import {
  getFileUris,
  isValidRegex,
  resetGlobCache,
  resolveSourcePath,
  resolveDestPath,
  updateTitle,
  syncWikilinks,
  syncWikilinksBatch,
} from '../util/util';


// result types

interface MoveSingleResult {
  relFrom: string;
  relTo: string;
  changed: string[];
  titleUpdatedFile?: string;
  errors: string[];
}

interface MoveRegexResult {
  moves: { relFrom: string; relTo: string }[];
  changed: string[];
  errors: string[];
}

// move command

export function move(fromArg: string, toArg: string, opts?: any): void {
  if (opts?.regex) {
    const result: MoveRegexResult | string = moveRegex(fromArg, toArg, opts);
    if (typeof result === 'string') {
      console.error(chalk.red(result));
    } else {
      formatRegexOutput(result, fromArg);
    }
  } else {
    const result: MoveSingleResult | string = moveSingle(fromArg, toArg, opts);
    if (typeof result === 'string') {
      console.error(chalk.red(result));
    } else {
      formatSingleOutput(result);
    }
  }
}

// single move

function moveSingle(fromArg: string, toArg: string, opts?: any): MoveSingleResult | string {
  const cwd: string = process.cwd();
  resetGlobCache();
  const sourcePath: string | undefined = resolveSourcePath(fromArg, cwd);
  if (sourcePath === undefined) {
    return `no such file: ${fromArg}`;
  }
  const destPath: string = resolveDestPath(toArg, cwd, sourcePath);
  if (path.resolve(sourcePath) === path.resolve(destPath)) {
    return 'source and destination are the same';
  }
  if (fs.existsSync(destPath)) {
    return `destination already exists: ${path.relative(cwd, destPath)}`;
  }
  const oldBasename: string = path.basename(sourcePath, MD);
  const newBasename: string = path.basename(destPath, MD);
  // move file
  fs.mkdirSync(path.dirname(destPath), { recursive: true });
  try {
    fs.renameSync(sourcePath, destPath);
  } catch (e: any) {
    return `move failed: ${e}`;
  }
  // update title
  const titleUpdated: boolean = (oldBasename !== newBasename)
    ? updateTitle(destPath, newBasename, opts)
    : false;
  // sync wikilinks
  resetGlobCache();
  const { changed, errors } = syncWikilinks(oldBasename, newBasename);
  return {
    relFrom: path.relative(cwd, sourcePath),
    relTo: path.relative(cwd, destPath),
    changed,
    titleUpdatedFile: titleUpdated ? newBasename : undefined,
    errors,
  };
}

// regex move — bulk pattern matching on relative paths

function moveRegex(fromPat: string, toPat: string, opts?: any): MoveRegexResult | string {
  if (!isValidRegex(fromPat)) {
    return '"from" is not a valid regex';
  }
  const cwd: string = process.cwd();
  const searchRe: RegExp = new RegExp(fromPat, 'g');
  const moves: { relFrom: string; relTo: string }[] = [];
  const errors: string[] = [];
  const renames: [string, string][] = [];
  const titleUpdatedFiles: Set<string> = new Set();
  // phase 1: move files + update titles
  const vaultFilePaths: string[] = getFileUris();
  for (const fp of vaultFilePaths) {
    const relPath: string = path.relative(cwd, fp);
    const relNoExt: string = relPath.replace(/\.md$/, '');
    if (!relNoExt.match(searchRe)) { continue; }
    const newRelNoExt: string = relNoExt.replace(searchRe, toPat);
    const destPath: string = path.resolve(cwd, newRelNoExt + MD);
    if (path.resolve(fp) === path.resolve(destPath)) { continue; }
    if (fs.existsSync(destPath)) {
      errors.push(`destination already exists: ${newRelNoExt}${MD}`);
      continue;
    }
    const oldBasename: string = path.basename(fp, MD);
    const newBasename: string = path.basename(destPath, MD);
    fs.mkdirSync(path.dirname(destPath), { recursive: true });
    try {
      fs.renameSync(fp, destPath);
      moves.push({ relFrom: relPath, relTo: newRelNoExt + MD });
      if (oldBasename !== newBasename) {
        renames.push([oldBasename, newBasename]);
        if (updateTitle(destPath, newBasename, opts)) {
          titleUpdatedFiles.add(newBasename);
        }
      }
    } catch (e: any) {
      errors.push(`move failed: ${e}`);
    }
  }
  // phase 2: batch sync wikilinks for basename changes
  const changed: Set<string> = new Set(titleUpdatedFiles);
  if (renames.length > 0) {
    resetGlobCache();
    const sync = syncWikilinksBatch(renames);
    for (const b of sync.changed) { changed.add(b); }
    for (const e of sync.errors) { errors.push(e); }
  }
  return {
    moves,
    changed: [...changed].sort(),
    errors,
  };
}

// output formatting

function formatSingleOutput(result: MoveSingleResult): void {
  for (const e of result.errors) {
    console.error(chalk.red('  ' + e));
  }
  const lines: string[] = [chalk.green(`moved ${result.relFrom} -> ${result.relTo}`)];
  const allChanged: Set<string> = new Set(result.changed);
  if (result.titleUpdatedFile) { allChanged.add(result.titleUpdatedFile); }
  if (allChanged.size > 0) {
    lines.push(chalk.green('UPDATED FILE CONTENT:'));
    for (const b of [...allChanged].sort()) {
      lines.push('  ' + b);
    }
  }
  console.log(lines.join('\n'));
}

function formatRegexOutput(result: MoveRegexResult, fromPat: string): void {
  for (const e of result.errors) {
    console.error(chalk.red('  ' + e));
  }
  const lines: string[] = [];
  if (result.moves.length === 0) {
    lines.push(chalk.dim('no files matched: \'' + fromPat + '\''));
  } else {
    for (const m of result.moves) {
      lines.push(chalk.green(`moved ${m.relFrom} -> ${m.relTo}`));
    }
  }
  if (result.changed.length > 0) {
    lines.push(chalk.green('UPDATED FILE CONTENT:'));
    for (const b of result.changed) {
      lines.push('  ' + b);
    }
  }
  console.log(lines.join('\n'));
}
